/**
 * Learning Packages List Page
 * Provides content creators with search, create, publish, copy-link, statistics, and delete actions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import {
  AppstoreAddOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  IdcardOutlined,
  PlusOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import PagesWrapper from '../../PagesWrapper';
import { getAllCountries } from '../../services/countriesService';
import {
  createLearningPackage,
  deleteLearningPackage,
  getLearningPackages,
  publishLearningPackage,
  unpublishLearningPackage,
} from '../../services/learningPackagesService';
import styles from './LearningPackages.module.css';

const { Search } = Input;
const { Text, Title } = Typography;

/**
 * Renders the authoring list for Learning Packages.
 * @returns {JSX.Element} Learning packages list page
 */
const LearningPackagesList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrPackage, setQrPackage] = useState(null);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  const loadCountries = useCallback(async () => {
    setCountriesLoading(true);
    try {
      const result = await getAllCountries({ suppressNotification: true });
      setCountries(result || []);
    } catch {
      message.error(t('learningPackages.countriesLoadError', 'Failed to load languages'));
    } finally {
      setCountriesLoading(false);
    }
  }, [t]);

  const loadPackages = useCallback(async (page = 1, searchTerm = '', pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const result = await getLearningPackages({
        page,
        pageSize,
        search: searchTerm || undefined,
      });
      setItems(result.items || []);
      setPagination(prev => ({
        ...prev,
        current: result.page || page,
        pageSize: result.pageSize || pageSize,
        total: result.totalCount || 0,
      }));
    } catch (error) {
      message.error(t('learningPackages.fetchError', 'Failed to load learning packages'));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, t]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const handleSearch = (value) => {
    setSearch(value);
    loadPackages(1, value, pagination.pageSize);
  };

  const handleTableChange = (paginationConfig) => {
    loadPackages(paginationConfig.current, search, paginationConfig.pageSize);
  };

  const handleRefresh = () => {
    loadPackages(pagination.current, search, pagination.pageSize);
  };

  const handleCreate = async () => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      const created = await createLearningPackage(values, { successMessage: t('learningPackages.createSuccess', 'Learning package created') });
      setCreateOpen(false);
      form.resetFields();
      navigate(`/learning-packages/${created.id}`);
    } finally {
      setCreating(false);
    }
  };

  const handlePublishToggle = async (record) => {
    try {
      if (record.isPublished) {
        await unpublishLearningPackage(record.id, { successMessage: t('learningPackages.unpublishSuccess', 'Learning package unpublished') });
      } else {
        await publishLearningPackage(record.id, { successMessage: t('learningPackages.publishSuccess', 'Learning package published'), suppressNotification: true });
      }
      loadPackages(pagination.current, search, pagination.pageSize);
    } catch (error) {
      // Publish validation failures (e.g. no published items) come back as 400 with a message.
      message.warning(error?.message || t('learningPackages.publishError', 'Failed to change publish state'));
    }
  };

  const handleCopyPublicLink = async (id) => {
    const url = `${window.location.origin}/packages/${id}`;
    await navigator.clipboard.writeText(url);
    message.success(t('learningPackages.linkCopied', 'Public link copied'));
  };

  const handleShowQrCode = (record) => {
    setQrPackage(record);
    setQrModalVisible(true);
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('learning-package-list-qr-code');
    if (!svg) {
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${qrPackage?.name || 'learning-package'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  const columns = [
    {
      title: t('learningPackages.columnName', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span className={styles.packageName} onClick={() => navigate(`/learning-packages/${record.id}`)}>
          {name}
        </span>
      ),
    },
    {
      title: t('learningPackages.columnItems', 'Items'),
      key: 'items',
      width: 280,
      render: (_, record) => (
        <div className={styles.itemCountTags}>
          <Tag color="blue">{record.pathCount} {t('learningPackages.paths', 'Paths')}</Tag>
          <Tag color="purple">{record.quizCollectionCount} {t('learningPackages.quizzes', 'Quizzes')}</Tag>
          <Tag color="cyan">{record.trainingProfileCount} {t('learningPackages.profiles', 'Profiles')}</Tag>
        </div>
      ),
    },
    {
      title: t('learningPackages.languageLabel', 'Language'),
      dataIndex: 'countryLanguage',
      key: 'countryLanguage',
      width: 140,
      render: (countryLanguage) => countryLanguage || '—',
    },
    {
      title: t('learningPackages.columnStatus', 'Status'),
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 130,
      render: (isPublished) => (
        <Tag
          color={isPublished ? 'green' : 'default'}
          icon={isPublished ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          className={styles.statusTag}
        >
          {isPublished ? t('learningPackages.published', 'Published') : t('learningPackages.draft', 'Draft')}
        </Tag>
      ),
    },
    {
      title: t('learningPackages.columnOwner', 'Owner'),
      dataIndex: 'ownerName',
      key: 'ownerName',
      width: 220,
      render: (ownerName, record) => (
        <Space size="small">
          <Avatar size="small" icon={<UserOutlined />} />
          <span>{ownerName || record.addedByName || '—'}</span>
          
        </Space>
      ),
    },
    {
      title: t('learningPackages.columnActions', 'Actions'),
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <Space size="small" className={styles.actions}>
          <Tooltip title={t('learningPackages.edit', 'Edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/learning-packages/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title={record.isPublished ? t('learningPackages.unpublish', 'Unpublish') : t('learningPackages.publish', 'Publish')}>
            <Button
              type="text"
              icon={record.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handlePublishToggle(record)}
            />
          </Tooltip>
          <Tooltip title={t('learningPackages.explorePackage', 'Explore Package')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              disabled={!record.isPublished}
              onClick={() => window.open(`/packages/${record.id}`, '_blank')}
            />
          </Tooltip>
          <Tooltip title={t('learningPackages.copyPublicLink', 'Copy public link')}>
            <Button
              type="text"
              icon={<CopyOutlined />}
              disabled={!record.isPublished}
              onClick={() => handleCopyPublicLink(record.id)}
            />
          </Tooltip>
          <Tooltip title={t('learningPackages.qrCode', 'QR Code')}>
            <Button
              type="text"
              icon={<QrcodeOutlined />}
              disabled={!record.isPublished}
              onClick={() => handleShowQrCode(record)}
            />
          </Tooltip>
          <Tooltip title={t('learningPackages.statistics', 'Statistics')}>
            <Button
              type="text"
              icon={<BarChartOutlined />}
              onClick={() => navigate(`/learning-packages/${record.id}/statistics`)}
            />
          </Tooltip>
          <Popconfirm
            title={t('learningPackages.deleteConfirm', 'Delete this learning package? Assigned content itself is not deleted.')}
            okText={t('learningPackages.delete', 'Delete')}
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              await deleteLearningPackage(record.id, { successMessage: t('learningPackages.deleteSuccess', 'Learning package deleted') });
              loadPackages(pagination.current, search, pagination.pageSize);
            }}
          >
            <Tooltip title={t('learningPackages.delete', 'Delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.listContainer}>
        <div className={styles.listHeader}>
          <div>
            <div className={styles.titleSection}>
              <AppstoreAddOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>{t('learningPackages.title', 'Learning Packages')}</Title>
            </div>
            <Text type="secondary" className={styles.listDescription}>
              {t('learningPackages.subtitle', 'Bundle learning paths, quizzes, and training profiles into curated learning offers')}
            </Text>
          </div>
        </div>

        <div className={styles.toolbar}>
          <Space>
            <Search
              placeholder={t('learningPackages.searchPlaceholder', 'Search packages...')}
              onSearch={handleSearch}
              allowClear
              style={{ width: 300 }}
            />
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              {t('common.refresh', 'Refresh')}
            </Button>
            <Button icon={<IdcardOutlined />} onClick={() => navigate('/learning-packages/roster')}>
              {t('learningPackages.openRoster', 'Visitor name roster')}
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('learningPackages.createButton', 'Create Package')}
          </Button>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          columns={columns}
          pagination={{
            ...pagination,
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <div className={styles.emptyState}>
                <p>{t('learningPackages.publicListEmpty', 'No published learning packages yet')}</p>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                  {t('learningPackages.createButton', 'Create Package')}
                </Button>
              </div>
            ),
          }}
        />

        <Modal
          title={t('learningPackages.createTitle', 'Create Learning Package')}
          open={createOpen}
          onCancel={() => setCreateOpen(false)}
          onOk={handleCreate}
          confirmLoading={creating}
          okText={t('learningPackages.createButton', 'Create Package')}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label={t('learningPackages.columnName', 'Name')}
              rules={[{ required: true, message: t('learningPackages.nameRequired', 'Name is required') }]}
            >
              <Input maxLength={255} placeholder={t('learningPackages.namePlaceholder', 'Package name')} />
            </Form.Item>
            <Form.Item
              name="countryId"
              label={t('learningPackages.languageLabel', 'Language')}
              rules={[{ required: true, message: t('learningPackages.languageRequired', 'Language is required') }]}
            >
              <Select
                showSearch
                loading={countriesLoading}
                placeholder={t('learningPackages.languagePlaceholder', 'Choose a language')}
                options={countries.map((country) => ({ value: country.id, label: country.language || country.name }))}
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label?.toLowerCase?.() || '').includes(input.toLowerCase())}
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={t('learningPackages.qrCode', 'QR Code')}
          open={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          centered
          footer={[
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadQrCode}>
              {t('learningPackages.downloadQrCode', 'Download QR Code')}
            </Button>,
            <Button key="close" onClick={() => setQrModalVisible(false)}>
              {t('common.close', 'Close')}
            </Button>,
          ]}
        >
          {qrPackage && (
            <div className={styles.qrCodeContainer}>
              <QRCodeSVG
                id="learning-package-list-qr-code"
                value={`${window.location.origin}/packages/${qrPackage.id}`}
                size={256}
                level="H"
                includeMargin
              />
              <p className={styles.qrCodeCollectionName}>{qrPackage.name}</p>
              <p className={styles.qrCodeLink}>{`${window.location.origin}/packages/${qrPackage.id}`}</p>
            </div>
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default LearningPackagesList;
